'use client'

import {
  FullBleedImage,
  OfferFrame,
  SafeText,
  clampIndex,
  getDesign,
  getOfferCopy,
  getOfferVisual,
  type OfferPackProps,
} from '../shared/OfferPackCore'

const bg = [
  'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1518611012118-696072aa579a?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1549476464-37392f717541?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1400&auto=format&fit=crop',
]

const total = 5
const sans = '"Montserrat", "Inter", Arial, sans-serif'

export default function FitnessOfferPerformancePack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
}: OfferPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getDesign(draft, '#B6FF3B')
  const accent = design.accent
  const orange = design.accent2 || '#FF6B35'
  const dark = '#101010'

  const copy = getOfferCopy(draft, index, {
    eyebrow: 'Performance offer',
    headline: 'Empieza fuerte. Sin improvisar.',
    subheadline: 'Un programa directo para activar disciplina, energía y resultados medibles.',
    offer: 'Plan de entrenamiento + guía de hábitos',
    proof: 'Más constancia. Mejor energía. Menos excusas.',
    urgency: 'Inscripciones abiertas esta semana',
    cta: 'Quiero entrar',
    bullets: ['Rutina semanal', 'Plan de hábitos', 'Seguimiento simple'],
  })

  return (
    <OfferFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      text={index === 1 ? '#111111' : '#ffffff'}
      className="bg-[#101010]"
    >
      {index === 0 ? (
        <>
          <FullBleedImage {...getOfferVisual(draft, index, bg[0], { overlay: 'black', opacity: 0.48 })} />
          <div className="absolute -left-24 top-0 h-full w-[58%] -skew-x-12" style={{ background: accent }} />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/18 to-black/74" />
          <div className="absolute left-8 top-8 text-[11px] font-black uppercase tracking-[0.18em] text-black" style={{ fontFamily: sans }}>
            {copy.eyebrow}
          </div>
          <div className="absolute bottom-14 left-8 max-w-[50%] text-black">
            <SafeText className="text-[clamp(40px,10vw,68px)] font-black uppercase leading-[0.86] tracking-[-0.06em]" style={{ fontFamily: sans }}>
              {copy.headline}
            </SafeText>
          </div>
          <div className="absolute bottom-16 right-8 max-w-[38%] text-white">
            <SafeText className="text-[15px] font-semibold leading-[1.38] text-white/78" style={{ fontFamily: sans }}>
              {copy.subheadline}
            </SafeText>
            <div className="mt-6 inline-flex rounded-full px-5 py-3 text-[11px] font-black uppercase tracking-[0.16em] text-black" style={{ background: accent, fontFamily: sans }}>
              {copy.cta}
            </div>
          </div>
        </>
      ) : null}

      {index === 1 ? (
        <>
          <div className="absolute inset-0" style={{ background: accent }} />
          <div className="absolute right-0 top-0 h-full w-[42%] overflow-hidden rounded-l-[54px]">
            <FullBleedImage {...getOfferVisual(draft, index, bg[1], { overlay: 'black', opacity: 0.16 })} />
          </div>
          <div className="absolute left-8 top-8 text-[11px] font-black uppercase tracking-[0.18em] text-black/62" style={{ fontFamily: sans }}>
            Incluye
          </div>
          <SafeText className="absolute left-8 top-20 max-w-[48%] text-[50px] font-black uppercase leading-[0.86] tracking-[-0.06em] text-black" style={{ fontFamily: sans }}>
            {copy.offer || 'Todo para arrancar sin fricción.'}
          </SafeText>
          <div className="absolute bottom-8 left-8 w-[52%] space-y-3">
            {(copy.bullets.length ? copy.bullets : ['Rutina', 'Hábitos', 'Seguimiento']).slice(0, 3).map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 border-t border-black/18 pt-3">
                <div className="text-[20px] font-black text-black" style={{ fontFamily: sans }}>0{idx + 1}</div>
                <SafeText className="text-[17px] font-black uppercase leading-[1] text-black" style={{ fontFamily: sans }}>{item}</SafeText>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {index === 2 ? (
        <>
          <div className="absolute inset-0" style={{ background: dark }} />
          <div className="absolute inset-x-8 top-8 flex justify-between border-b border-white/16 pb-5 text-[11px] font-black uppercase tracking-[0.2em] text-white/56" style={{ fontFamily: sans }}>
            <span>Resultado</span>
            <span>Sin excusas</span>
          </div>
          <div className="absolute left-8 top-24 max-w-[68%] text-white">
            <SafeText className="text-[54px] font-black uppercase leading-[0.88] tracking-[-0.06em]" style={{ fontFamily: sans }}>
              {copy.proof || 'La constancia se diseña. No se espera.'}
            </SafeText>
            <SafeText className="mt-6 max-w-[72%] text-[15px] font-semibold leading-[1.42] text-white/64" style={{ fontFamily: sans }}>
              {copy.body || copy.subheadline}
            </SafeText>
          </div>
          <div className="absolute bottom-8 right-8 text-[96px] font-black leading-none" style={{ color: accent, fontFamily: sans }}>
            +
          </div>
          <div className="absolute bottom-12 right-24 h-[3px] w-28" style={{ background: orange }} />
        </>
      ) : null}

      {index === 3 ? (
        <>
          <FullBleedImage {...getOfferVisual(draft, index, bg[3], { overlay: 'black', opacity: 0.5, blur: true })} />
          <div className="absolute inset-0 bg-gradient-to-r from-black/86 via-black/46 to-transparent" />
          <div className="absolute left-8 top-10 max-w-[62%] text-white">
            <div className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: accent, fontFamily: sans }}>Urgencia</div>
            <SafeText className="mt-6 text-[50px] font-black uppercase leading-[0.88] tracking-[-0.06em]" style={{ fontFamily: sans }}>
              {copy.urgency || 'Esta semana se abre el siguiente bloque.'}
            </SafeText>
          </div>
          <div className="absolute bottom-10 left-8 rounded-full px-6 py-3 text-[11px] font-black uppercase tracking-[0.16em] text-black" style={{ background: accent, fontFamily: sans }}>
            Entrar ahora
          </div>
        </>
      ) : null}

      {index === 4 ? (
        <>
          <FullBleedImage {...getOfferVisual(draft, index, bg[4], { overlay: 'black', opacity: 0.58 })} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/88 via-black/28 to-transparent" />
          <div className="absolute left-8 right-8 top-10 flex items-center justify-between text-[11px] font-black uppercase tracking-[0.22em] text-white/56" style={{ fontFamily: sans }}>
            <span>Ready</span>
            <span>Offer</span>
          </div>
          <div className="absolute bottom-16 left-8 right-8 text-white">
            <SafeText className="max-w-[72%] text-[62px] font-black uppercase leading-[0.84] tracking-[-0.065em]" style={{ fontFamily: sans }}>
              {copy.cta || 'Entra al programa'}
            </SafeText>
            <div className="mt-7 flex items-center gap-3">
              <div className="rounded-full px-6 py-3 text-[11px] font-black uppercase tracking-[0.16em] text-black" style={{ background: accent, fontFamily: sans }}>
                Comenzar
              </div>
              <div className="text-[13px] font-semibold text-white/65" style={{ fontFamily: sans }}>
                {copy.urgency}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </OfferFrame>
  )
}
